import {
  Box,
  Button,
  Divider,
  Input,
  LinearProgress,
  Sheet,
  Typography,
} from "@mui/joy";
import { DialogActions, DialogContent, DialogTitle } from "@mui/material";
import stringEntropy from "fast-password-entropy";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { formOpenSelector, setFormOpen } from "../../reducers/rhapsody";
import { useCurrentUser } from "../../redux/user/userHook";
import DialogClose from "../dialogClose/dialogClose";
import { useAskQuestion } from "../askQuestion/askQuestion";
import { useUpdateUserPasswordMutation } from "../../redux/user/userEndpoint";
import {
  DialogHeader,
  FieldLabel,
  GroupCaption,
  dialogPaperSx,
  groupSheetSx,
  pillButtonSx,
  tileColors,
} from "../formKit/formKit";

enum PasswordStrength {
  weak,
  average,
  strong,
}

/**
 *
 * @returns {ReactElement} ChangePassword page
 */
export function ChangePassword() {
  const open = useSelector(formOpenSelector("changePassword"));
  const dispatch = useDispatch();
  const { me: data } = useCurrentUser();
  const [reveal, setReveal] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const askQuestion = useAskQuestion();
  const [updateUserPassword] = useUpdateUserPasswordMutation();

  const onClose = () => {
    dispatch(setFormOpen({ formID: "changePassword", isOpen: false }));
  };

  function getPasswordStrength(password: string): {
    label: string;
    color: string;
    level: "danger" | "success" | "warning";
    entropy: number;
  } {
    const entropy = stringEntropy(password);
    if (entropy < 50)
      return {
        label: "Password is weak",
        color: "#f44336",
        level: "danger",
        entropy: entropy,
      };
    if (entropy < 90)
      return {
        label: "Password is average",
        color: "#ff9800",
        level: "warning",
        entropy: entropy,
      };
    return {
      label: "Password is strong",
      color: "#4caf50",
      level: "success",
      entropy: entropy,
    };
  }

  const strengh = getPasswordStrength(password);

  const request = async () => {
    if (strengh.entropy >= 90) {
      updateUserPassword({ body: { password } });
      askQuestion("Success", ["OK"], {
        subtitle: <Typography>Password has been changed.</Typography>,
      });

      setPassword("");
      setPasswordConfirm("");
      setReveal(false);
      onClose();
    } else {
      askQuestion("Warning", ["OK"], {
        subtitle: (
          <Typography>
            Your password is too weak or the two fields don't match.
          </Typography>
        ),
      });
    }
  };

  return (
    <DialogClose
      open={open}
      onClose={onClose}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle>
        <DialogHeader
          title="Change Password"
          subtitle="Pick something strong — only you should know it."
        />
      </DialogTitle>
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: 440,
          maxWidth: "100%",
        }}
      >
        <GroupCaption>New password</GroupCaption>
        <Sheet variant="soft" sx={groupSheetSx}>
          <Input
            sx={{ background: "transparent" }}
            variant="plain"
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type={reveal ? "text" : "password"}
            endDecorator={
              <Button
                onClick={() => setReveal((a) => !a)}
                variant="plain"
                color="neutral"
                size="sm"
              >
                <i
                  className={
                    reveal ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"
                  }
                ></i>
              </Button>
            }
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-key"
                color={tileColors.orange}
                label="Password"
              />
            }
          />
          <Divider />
          <Input
            sx={{ background: "transparent" }}
            variant="plain"
            slotProps={{
              input: { style: { textAlign: "right" } },
            }}
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            type={reveal ? "text" : "password"}
            endDecorator={
              <Button
                onClick={() => setReveal((a) => !a)}
                variant="plain"
                color="neutral"
                size="sm"
              >
                <i
                  className={
                    reveal ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"
                  }
                ></i>
              </Button>
            }
            startDecorator={
              <FieldLabel
                icon="fa-solid fa-lock"
                color={tileColors.gray}
                label="Confirm Password"
              />
            }
          />
        </Sheet>
        {password ? (
          <Box sx={{ px: 0.5 }}>
            <Typography sx={{ color: strengh.color }}>
              {strengh.label}
            </Typography>
            <LinearProgress
              color={strengh.level}
              determinate
              value={strengh.entropy}
            />
          </Box>
        ) : (
          []
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="plain"
          color="neutral"
          sx={pillButtonSx}
        >
          Cancel
        </Button>
        <Button
          onClick={request}
          disabled={strengh.entropy < 90 || password !== passwordConfirm}
          variant="solid"
          color="primary"
          sx={pillButtonSx}
        >
          Save Password
        </Button>
      </DialogActions>
    </DialogClose>
  );
}
